import { useState, useEffect, useRef } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Trophy, Eye, EyeOff, AlertCircle, CheckCircle2, Home, ShieldCheck, Download } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { authAPI } from '@/lib/api'
import usePageTitle from '@/lib/usePageTitle'
import { useAuth } from '@/hooks/useAuth'
import { useFavicon } from '@/hooks/useFavicon'
import PubliekeModusBanner from '@/components/PubliekeModusBanner'

export default function LoginPage() {
  usePageTitle('Inloggen')
  useFavicon(null)
  const navigate = useNavigate()
  const { updateUser } = useAuth()
  const usernameInputRef = useRef(null)

  // Form states
  const [loginData, setLoginData] = useState({
    username: '',
    password: '',
  })

  const [registerData, setRegisterData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
  })

  // UI states
  const [showLoginPassword, setShowLoginPassword] = useState(false)
  const [showRegisterPassword, setShowRegisterPassword] = useState(false)
  // Registratie is standaard uit — deelnemers komen uit de kassa-import.
  // De admin kan het aanzetten in het adminpaneel; dan verschijnt de tab.
  const [regAan, setRegAan] = useState(false)

  useEffect(() => {
    fetch('/api/settings/security')
      .then(r => r.json())
      .then(d => setRegAan(!!d?.registration_enabled))
      .catch(() => setRegAan(false))
  }, [])
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [activeTab, setActiveTab] = useState('login')

  // Focus first input on mount
  useEffect(() => {
    if (usernameInputRef.current) {
      usernameInputRef.current.focus()
    }
  }, [activeTab])

  // Clear messages when switching tabs
  useEffect(() => {
    setError('')
    setSuccess('')
  }, [activeTab])

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const validatePassword = (password) => {
    return password.length >= 8
  }

  const validateLoginForm = () => {
    if (!loginData.username.trim()) { setError('Gebruikersnaam is verplicht'); return false }
    if (!loginData.password)         { setError('Wachtwoord is verplicht'); return false }
    return true
  }

  const validateRegisterForm = () => {
    if (!registerData.username.trim())       { setError('Gebruikersnaam is verplicht'); return false }
    if (registerData.username.length < 3)    { setError('Gebruikersnaam moet minstens 3 tekens hebben'); return false }
    if (!registerData.email.trim())          { setError('E-mail is verplicht'); return false }
    if (!validateEmail(registerData.email))  { setError('Geef een geldig e-mailadres op'); return false }
    if (!registerData.password)              { setError('Wachtwoord is verplicht'); return false }
    if (!validatePassword(registerData.password)) { setError('Wachtwoord moet minstens 8 tekens hebben'); return false }
    if (registerData.password !== registerData.confirmPassword) { setError('Wachtwoorden komen niet overeen'); return false }
    return true
  }

  // Handle login
  const handleLogin = async (e) => {
    e.preventDefault()
    setError('')
    setSuccess('')

    if (!validateLoginForm()) {
      return
    }

    setIsLoading(true)

    try {
      const response = await authAPI.login({
        username: loginData.username,
        password: loginData.password,
      })

      // Store token and user info
      const { token, user } = response.data
      localStorage.setItem('auth_token', token)
      updateUser(user)

      // Show success message
      setSuccess('Ingelogd — even geduld…')

      // Redirect to home page after short delay
      setTimeout(() => {
        navigate('/')
      }, 1000)

    } catch (err) {
      console.error('Login error:', err)
      const errorMessage = err.response?.data?.message || err.response?.data?.error || 'Inloggen mislukt — check je gebruikersnaam en wachtwoord.'
      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  // Handle register
  const handleRegister = async (e) => {
    e.preventDefault()
    setError('')
    setSuccess('')

    if (!validateRegisterForm()) {
      return
    }

    setIsLoading(true)

    try {
      const response = await authAPI.register({
        username: registerData.username,
        email: registerData.email,
        password: registerData.password,
      })

      // Store token and user info
      const { token, user } = response.data
      localStorage.setItem('auth_token', token)
      updateUser(user)

      // Show success message
      setSuccess('Account aangemaakt — even geduld…')

      // Redirect to home page after short delay
      setTimeout(() => {
        navigate('/')
      }, 1000)

    } catch (err) {
      console.error('Registration error:', err)
      const errorMessage = err.response?.data?.message || err.response?.data?.error || 'Registratie mislukt — probeer opnieuw.'
      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  // Handle input changes
  const handleLoginChange = (e) => {
    setLoginData({
      ...loginData,
      [e.target.name]: e.target.value,
    })
  }

  const handleRegisterChange = (e) => {
    setRegisterData({
      ...registerData,
      [e.target.name]: e.target.value,
    })
  }

  // Handle Enter key submission
  const handleKeyPress = (e, formType) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (formType === 'login') {
        handleLogin(e)
      } else {
        handleRegister(e)
      }
    }
  }

  return (
    <>
      <PubliekeModusBanner />
      <div className="min-h-screen bg-gaming-gradient flex items-center justify-center p-4">
      {/* Home Button - Top Left */}
      <Link
        to="/"
        className="absolute top-4 left-4 flex items-center space-x-2 px-4 py-2 rounded-md bg-background/80 border border-neon-blue/30 text-neon-blue hover:bg-neon-blue/10 hover:border-neon-blue/50 transition-all shadow-lg backdrop-blur"
      >
        <Home className="h-4 w-4" />
        <span className="font-medium">Home</span>
      </Link>

      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center h-16 w-16 rounded-lg bg-gradient-to-br from-neon-blue to-neon-purple mb-4 shadow-neon-blue">
            <Trophy className="h-10 w-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold mb-2">LAN Server</h1>
          <p className="text-muted-foreground">
            Welkom op de LAN
          </p>
        </div>

        {/* Auth Card */}
        <Card className="border-neon shadow-neon-blue">
          <CardHeader>
            <CardTitle className="">Aanmelden</CardTitle>
            <CardDescription>
              Log in met je account of maak een nieuwe aan
            </CardDescription>
          </CardHeader>

          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className={`grid w-full ${regAan ? 'grid-cols-2' : 'grid-cols-1'} bg-background/50 border border-neon-blue/30`}>
                <TabsTrigger
                  value="login"
                  className="data-[state=active]:bg-neon-blue/20 data-[state=active]:text-neon-blue data-[state=active]:shadow-neon-blue transition-all"
                >
                  Inloggen
                </TabsTrigger>
                {regAan && (<TabsTrigger
                  value="register"
                  className="data-[state=active]:bg-neon-purple/20 data-[state=active]:text-neon-purple data-[state=active]:shadow-neon-purple transition-all"
                >
                  Registreren
                </TabsTrigger>)}
              </TabsList>

              {/* Login Tab */}
              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  {/* Error Alert */}
                  {error && activeTab === 'login' && (
                    <Alert variant="destructive" className="border-destructive/50 bg-destructive/10">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}

                  {/* Success Alert */}
                  {success && activeTab === 'login' && (
                    <Alert className="border-green-500/50 bg-green-500/10 text-green-500">
                      <CheckCircle2 className="h-4 w-4" />
                      <AlertDescription>{success}</AlertDescription>
                    </Alert>
                  )}

                  {/* Username */}
                  <div className="space-y-2">
                    <Label htmlFor="login-username">Gebruikersnaam</Label>
                    <Input
                      id="login-username"
                      name="username"
                      type="text"
                      placeholder="Je gebruikersnaam"
                      value={loginData.username}
                      onChange={handleLoginChange}
                      onKeyPress={(e) => handleKeyPress(e, 'login')}
                      disabled={isLoading}
                      required
                      ref={usernameInputRef}
                      className="bg-background border-neon focus-visible:ring-neon-blue focus-visible:ring-2 focus-visible:shadow-neon-blue transition-all"
                    />
                  </div>

                  {/* Password */}
                  <div className="space-y-2">
                    <Label htmlFor="login-password">Wachtwoord</Label>
                    <div className="relative">
                      <Input
                        id="login-password"
                        name="password"
                        type={showLoginPassword ? 'text' : 'password'}
                        placeholder="Je wachtwoord"
                        value={loginData.password}
                        onChange={handleLoginChange}
                        onKeyPress={(e) => handleKeyPress(e, 'login')}
                        disabled={isLoading}
                        required
                        className="bg-background border-neon focus-visible:ring-neon-blue focus-visible:ring-2 focus-visible:shadow-neon-blue transition-all pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowLoginPassword(!showLoginPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-neon-blue transition-colors"
                        tabIndex={-1}
                      >
                        {showLoginPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    className="w-full bg-neon-blue/20 hover:bg-neon-blue/30 text-neon-blue border border-neon-blue/50 shadow-neon-blue hover:shadow-neon-blue-lg transition-all font-semibold"
                    disabled={isLoading}
                  >
                    {isLoading ? 'Bezig…' : 'Inloggen'}
                  </Button>
                </form>
              </TabsContent>

              {/* Register Tab */}
              {regAan && (<TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-4">
                  {/* Error Alert */}
                  {error && activeTab === 'register' && (
                    <Alert variant="destructive" className="border-destructive/50 bg-destructive/10">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}

                  {/* Success Alert */}
                  {success && activeTab === 'register' && (
                    <Alert className="border-green-500/50 bg-green-500/10 text-green-500">
                      <CheckCircle2 className="h-4 w-4" />
                      <AlertDescription>{success}</AlertDescription>
                    </Alert>
                  )}

                  {/* Username */}
                  <div className="space-y-2">
                    <Label htmlFor="register-username">Gebruikersnaam</Label>
                    <Input
                      id="register-username"
                      name="username"
                      type="text"
                      placeholder="Kies een gebruikersnaam"
                      value={registerData.username}
                      onChange={handleRegisterChange}
                      onKeyPress={(e) => handleKeyPress(e, 'register')}
                      disabled={isLoading}
                      required
                      ref={usernameInputRef}
                      className="bg-background border-neon focus-visible:ring-neon-purple focus-visible:ring-2 focus-visible:shadow-neon-purple transition-all"
                    />
                  </div>

                  {/* Email */}
                  <div className="space-y-2">
                    <Label htmlFor="register-email">E-mail</Label>
                    <Input
                      id="register-email"
                      name="email"
                      type="email"
                      placeholder="Je e-mailadres"
                      value={registerData.email}
                      onChange={handleRegisterChange}
                      onKeyPress={(e) => handleKeyPress(e, 'register')}
                      disabled={isLoading}
                      required
                      className="bg-background border-neon focus-visible:ring-neon-purple focus-visible:ring-2 focus-visible:shadow-neon-purple transition-all"
                    />
                  </div>

                  {/* Password */}
                  <div className="space-y-2">
                    <Label htmlFor="register-password">Wachtwoord</Label>
                    <div className="relative">
                      <Input
                        id="register-password"
                        name="password"
                        type={showRegisterPassword ? 'text' : 'password'}
                        placeholder="Minstens 8 tekens"
                        value={registerData.password}
                        onChange={handleRegisterChange}
                        onKeyPress={(e) => handleKeyPress(e, 'register')}
                        disabled={isLoading}
                        required
                        className="bg-background border-neon focus-visible:ring-neon-purple focus-visible:ring-2 focus-visible:shadow-neon-purple transition-all pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowRegisterPassword(!showRegisterPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-neon-purple transition-colors"
                        tabIndex={-1}
                      >
                        {showRegisterPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Confirm Password */}
                  <div className="space-y-2">
                    <Label htmlFor="register-confirm-password">Wachtwoord bevestigen</Label>
                    <div className="relative">
                      <Input
                        id="register-confirm-password"
                        name="confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'}
                        placeholder="Herhaal je wachtwoord"
                        value={registerData.confirmPassword}
                        onChange={handleRegisterChange}
                        onKeyPress={(e) => handleKeyPress(e, 'register')}
                        disabled={isLoading}
                        required
                        className="bg-background border-neon focus-visible:ring-neon-purple focus-visible:ring-2 focus-visible:shadow-neon-purple transition-all pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-neon-purple transition-colors"
                        tabIndex={-1}
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    className="w-full bg-neon-purple/20 hover:bg-neon-purple/30 text-neon-purple border border-neon-purple/50 shadow-neon-purple hover:shadow-neon-purple-lg transition-all font-semibold"
                    disabled={isLoading}
                  >
                    {isLoading ? 'Aanmaken…' : 'Account aanmaken'}
                  </Button>
                </form>
              </TabsContent>)}
            </Tabs>
          </CardContent>

          <CardFooter className="flex flex-col space-y-2 text-center text-sm text-muted-foreground">
            <p>{(() => { try { return JSON.parse(sessionStorage.getItem("brandingCache")||"null")?.footer_text } catch{}; return null })() || "LAN Server · thuisserver op Proxmox"}</p>
            <p className="text-xs">Beveiligd via JWT</p>
          </CardFooter>
        </Card>

        {/* SSL certificaat download */}
        <div className="w-full max-w-md mt-4 rounded-lg border border-neon-blue/20 bg-neon-blue/5 p-4">
          <div className="flex items-start gap-3">
            <ShieldCheck className="h-5 w-5 text-neon-blue mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-neon-blue">Beveiligde verbinding instellen</p>
              <p className="text-xs text-muted-foreground mt-1">
                Installeer het LAN Server certificaat om de browser-waarschuwing te verwijderen en veilig te downloaden.
              </p>
              <div className="mt-3 flex flex-col gap-2">
                <a
                  href="/lanserver.crt"
                  download="lanserver.crt"
                  className="inline-flex items-center gap-2 text-xs bg-neon-blue/10 hover:bg-neon-blue/20 text-neon-blue border border-neon-blue/30 rounded px-3 py-1.5 transition-colors w-fit"
                >
                  <Download className="h-3 w-3" /> Download certificaat (lanserver.crt)
                </a>
                <p className="text-xs text-muted-foreground">
                  Na download: dubbelklik op het bestand → <span className="text-foreground">Installeer certificaat</span> → Lokale computer → <span className="text-foreground">Vertrouwde basiscertificeringsinstanties</span>
                </p>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
    </>
  )
}
